
export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
export type NormalBalance = 'DR' | 'CR';
export type ControlKind =
  | 'CASH'
  | 'BANK'
  | 'RECEIVABLE'
  | 'PAYABLE'
  | 'INVENTORY'
  | 'TAX'
  | 'COMMISSION_RECEIVABLE'
  | 'COMMISSION_PAYABLE'
  | 'FIXED_ASSET';

export interface Account {
  code: string;
  name: string;
  type: AccountType;
  normalBalance: NormalBalance;
  control: ControlKind | null;
  allowDirectPosting: boolean;
  fcyCurrency: string | null;
  requiresParty: boolean;
  isLeaf: boolean;
  level: number;
  parentCode: string | null;
  childCount: number;
  balanceBase: string;
  isBank: boolean;
}

export const SUB_ACCOUNT_PARENT_LEVEL = 4;

let INDEX = new Map<string, Account>();
let LIST: Account[] = [];
let CHILDREN = new Map<string, Account[]>();

export function setAccounts(accounts: Account[]): void {
  LIST = accounts;
  INDEX = new Map(accounts.map((account) => [account.code, account]));

  CHILDREN = new Map();
  for (const account of accounts) {
    if (!account.parentCode) continue;
    const siblings = CHILDREN.get(account.parentCode);
    if (siblings) siblings.push(account);
    else CHILDREN.set(account.parentCode, [account]);
  }
}

export function allAccounts(): Account[] {
  return LIST;
}

export function postableAccounts(): Account[] {
  return LIST.filter((account) => account.isLeaf);
}

export function subAccountsOf(code: string): Account[] {
  return CHILDREN.get(code) ?? [];
}

export function hasSubAccounts(code: string): boolean {
  return (CHILDREN.get(code)?.length ?? 0) > 0;
}

export function entryAccounts(): Account[] {
  return LIST.filter((account) =>
    account.isLeaf
      ? account.level <= SUB_ACCOUNT_PARENT_LEVEL
      : account.level === SUB_ACCOUNT_PARENT_LEVEL && account.childCount > 0,
  );
}

export function splitPostingAccount(code: string): {
  accountCode: string;
  subAccountCode: string;
} {
  const account = INDEX.get(code);
  if (account?.parentCode && account.level > SUB_ACCOUNT_PARENT_LEVEL) {
    return { accountCode: account.parentCode, subAccountCode: code };
  }
  return { accountCode: code, subAccountCode: '' };
}

export function accountsOfKind(kind: 'CASH' | 'BANK'): Account[] {
  return LIST.filter(
    (account) =>
      account.isLeaf && (account.control === kind || (kind === 'BANK' && account.isBank)),
  );
}

export function defaultAccountOfKind(kind: 'CASH' | 'BANK'): string {
  return accountsOfKind(kind)[0]?.code ?? '';
}

export function findAccount(code: string): Account | undefined {
  return INDEX.get(code);
}

export function accountLabel(code: string): string {
  const account = INDEX.get(code);
  return account ? `${account.code} · ${account.name}` : code;
}


export interface Party {
  code: string;
  name: string;
  kind: 'CUSTOMER' | 'SUPPLIER' | 'BROKER_SELLER' | 'SALESPERSON';
}


export const PARTIES: Party[] = [
  {
    code: 'CUS-0001',
    name: 'Sadiq Poultry Farm',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0002',
    name: 'Rahim Traders',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0003',
    name: 'Chenab Feeds Limited',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0004',
    name: 'Hilal Poultry Supply',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0005',
    name: 'Al-Noor Broilers',
    kind: 'CUSTOMER',
  },
  {
    code: 'CUS-0006',
    name: 'Frontier Feed Mills',
    kind: 'CUSTOMER',
  },
  { code: 'SUP-0001', name: 'Punjab Oil Mills', kind: 'SUPPLIER' },
  { code: 'SUP-0002', name: 'Indus Corn Products', kind: 'SUPPLIER' },
  { code: 'SUP-0003', name: 'Karachi Clearing Agency', kind: 'SUPPLIER' },
  { code: 'SUP-1001', name: 'Kwan Agro Ltd', kind: 'SUPPLIER' },
  { code: 'SUP-1002', name: 'Rosario Grain Export', kind: 'SUPPLIER' },
  { code: 'SUP-1003', name: 'Paranagua Soy Trading', kind: 'SUPPLIER' },
  { code: 'SUP-1004', name: 'Gulf Marine Proteins', kind: 'SUPPLIER' },
  { code: 'PTA-0001', name: 'Sadiq Feed Mills', kind: 'BROKER_SELLER' },
  { code: 'PTA-0002', name: 'Andes Protein SA', kind: 'BROKER_SELLER' },
  { code: 'PTA-0003', name: 'Delta Grain FZE', kind: 'BROKER_SELLER' },
  {
    code: 'SP-01',
    name: 'Kamran Sheikh (salesperson)',
    kind: 'SALESPERSON',
  },
  { code: 'SP-02', name: 'Bilal Ahmed (salesperson)', kind: 'SALESPERSON' },
];

const PARTY_INDEX = new Map(PARTIES.map((party) => [party.code, party]));

export function findParty(code: string): Party | undefined {
  return PARTY_INDEX.get(code);
}

export function partyLabel(code: string): string {
  const party = PARTY_INDEX.get(code);
  return party ? `${party.name} (${party.code})` : code;
}


export const BRANCHES = [
  { code: 'HO', name: 'Gujranwala' },
  { code: 'LHR', name: 'Lahore Branch' },
];


export interface CompanyRef {
  code: string;
  name: string;
  legalName: string;
  tagline: string | null;
  brandMark: string | null;
  logoUrl: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  printShowNtn: boolean;
  printShowStrn: boolean;
  printShowCuin: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  reportFooter: string | null;
  baseCurrency: string;
  timezone: string;
  defaultUomCode: string | null;
}

let COMPANY: CompanyRef | null = null;

export function setCompany(company: CompanyRef | null): void {
  COMPANY = company;
}

export function company(): CompanyRef | null {
  return COMPANY;
}

export function printIdentifiers(): Array<{ label: string; value: string }> {
  const co = COMPANY;
  if (!co) return [];
  return [
    { label: 'NTN', value: co.printShowNtn ? (co.ntn ?? '') : '' },
    { label: 'STRN', value: co.printShowStrn ? (co.strn ?? '') : '' },
    { label: 'CUIN', value: co.printShowCuin ? (co.cuin ?? '') : '' },
  ].filter((i) => i.value !== '');
}

export function companyTimezone(): string {
  return COMPANY?.timezone || 'Asia/Karachi';
}

export function companyLocation(): string {
  const co = COMPANY;
  if (!co) return '';
  return [co.addressLine1, co.addressLine2, co.city, co.province].filter(Boolean).join(', ');
}


export interface UomRef {
  code: string;
  name: string;
  dimension: string;
  decimals: number;
  factorToBase?: string;
}

let UOMS: UomRef[] = [];

export function setUoms(uoms: UomRef[]): void {
  UOMS = uoms;
}

export function allUoms(): UomRef[] {
  return UOMS;
}

export function uomOptions(current?: string, baseUom?: string | null): UomRef[] {
  const base = baseUom ? UOMS.find((u) => u.code === baseUom) : undefined;
  const offered = base ? UOMS.filter((u) => u.dimension === base.dimension) : UOMS;
  if (!current || offered.some((u) => u.code === current)) return offered;
  const held = UOMS.find((u) => u.code === current);
  return [...offered, held ?? { code: current, name: current, dimension: 'WEIGHT', decimals: 3 }];
}

export function baseUomOf(
  sku: string,
  products: Array<{ sku: string; baseUom?: string }> | undefined,
): string | undefined {
  if (!sku) return undefined;
  return (products ?? []).find((p) => p.sku === sku)?.baseUom;
}

export function defaultUom(productStockUom?: string | null): string {
  if (productStockUom && UOMS.some((u) => u.code === productStockUom)) return productStockUom;
  const house = COMPANY?.defaultUomCode;
  if (house && UOMS.some((u) => u.code === house)) return house;
  return UOMS[0]?.code ?? '';
}


export type PeriodStatus = 'OPEN' | 'CLOSED' | 'FUTURE';

export interface Period {
  periodNo: number;
  name: string;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  isAdjustment?: boolean;
}

export interface FiscalYearRef {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

let FY: FiscalYearRef | null = null;
let PERIOD_LIST: Period[] = [];

export function setFiscalCalendar(year: FiscalYearRef | null, periods: Period[]): void {
  FY = year;
  PERIOD_LIST = periods;
}

export function fiscalYear(): FiscalYearRef {
  return FY ?? { code: '—', name: 'No open fiscal year', startDate: '', endDate: '' };
}

export function hasFiscalCalendar(): boolean {
  return FY !== null;
}

export function periods(): Period[] {
  return PERIOD_LIST;
}

export function resolvePeriod(voucherDate: string): Period | undefined {
  return PERIOD_LIST.find(
    (p) => !p.isAdjustment && voucherDate >= p.startDate && voucherDate <= p.endDate,
  );
}

export function defaultVoucherDate(now = new Date()): string {
  const today = companyToday(now);
  if (resolvePeriod(today)?.status === 'OPEN') return today;
  return PERIOD_LIST.find((p) => p.status === 'OPEN')?.endDate || fiscalYear().endDate || today;
}

export function monthOf(date: string): { from: string; to: string } {
  const [y, m] = date.split('-').map(Number);
  const year = y ?? new Date().getUTCFullYear();
  const month = m ?? 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

export function shiftMonth(from: string, offset: number): { from: string; to: string } {
  const [y, m] = from.split('-').map(Number);
  const anchor = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + offset, 1));
  return monthOf(anchor.toISOString().slice(0, 10));
}

export function fiscalYearToDate(): { from: string; to: string } {
  const to = companyToday();
  const fy = fiscalYear();
  const from = fy.startDate || `${to.slice(0, 4)}-01-01`;
  return { from: from > to ? to : from, to };
}

export function companyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: companyTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}


export const SPOT_USD_PKR = '278.4000';

export interface CurrencyRef {
  code: string;
  name: string;
  isBase: boolean;
  rate: string | null;
}

let CURRENCIES: CurrencyRef[] = [];
let BASE_CURRENCY = 'PKR';

export function setCurrencies(currencies: CurrencyRef[]): void {
  CURRENCIES = currencies;
  BASE_CURRENCY = currencies.find((c) => c.isBase)?.code ?? BASE_CURRENCY;
}

export function allCurrencies(): CurrencyRef[] {
  return CURRENCIES;
}

export function baseCurrency(): string {
  return BASE_CURRENCY;
}

export function rateFor(code: string): string | null {
  if (code === BASE_CURRENCY) return '1';
  return CURRENCIES.find((c) => c.code === code)?.rate ?? null;
}

